/**
 * ODMIN: shared .odx-debug-panel chrome (float toggle, drag, resize, odx-debug-panel-added).
 * Contract: feature markup lives only under .odx-debug-panel__body; init normalizes legacy roots by
 * wrapping direct children. JS injectors should create .odx-debug-panel__body (or call odxDebugPanelEnsureBody).
 * Drag + resize use Pointer Events + setPointerCapture so touch works, not mouse-only.
 * While dragging/resizing, document touchmove is blocked (passive:false) so the page does not scroll on mobile.
 * Floating position is clamped so at least 30px of the panel stays on-screen on each axis.
 * Loaded from odx-head-scripts when safe, or injected on loopback hostname when Liquid safe is false.
 */
(function () {
  function ensureDebugPanelBody(panel) {
    var body = panel.querySelector(':scope > .odx-debug-panel__body');
    if (body) return body;
    body = document.createElement('div');
    body.className = 'odx-debug-panel__body';
    while (panel.firstChild) {
      body.appendChild(panel.firstChild);
    }
    panel.appendChild(body);
    return body;
  }

  /** @returns {HTMLElement|null} */
  window.odxDebugPanelEnsureBody = function (panel) {
    if (!panel || !panel.classList || !panel.classList.contains('odx-debug-panel')) return null;
    return ensureDebugPanelBody(panel);
  };

  var touchScrollBlockOpts = { capture: true, passive: false };
  var touchScrollBlockCount = 0;
  function touchScrollBlock(ev) {
    ev.preventDefault();
  }
  function addTouchScrollBlock() {
    if (touchScrollBlockCount++ === 0) {
      document.addEventListener('touchmove', touchScrollBlock, touchScrollBlockOpts);
    }
  }
  function removeTouchScrollBlock() {
    if (--touchScrollBlockCount <= 0) {
      touchScrollBlockCount = 0;
      document.removeEventListener('touchmove', touchScrollBlock, touchScrollBlockOpts);
    }
  }

  /**
   * Ref count for document-level `touchmove` preventDefault while dragging/resizing ODMIN debug panels.
   * PDP scroll bug: used by variant PDP zoom debug — do not remove until maintainer declares that bug solved.
   */
  window.odxDebugPanelGetTouchScrollBlockCount = function () {
    return touchScrollBlockCount;
  };

  function initPanel(panel) {
    if (panel.dataset.odxPanelInited === '1') return;
    panel.dataset.odxPanelInited = '1';
    var panelBody = ensureDebugPanelBody(panel);
    var isFloating = false;
    var placeholder = null;
    var dragOffset = { x: 0, y: 0 };
    var resizeHandles = {};
    var VIEWPORT_EDGE_KEEP_PX = 30;
    function parsePx(s) {
      var n = parseFloat(String(s || '').replace(/px\s*$/i, ''));
      return isNaN(n) ? null : n;
    }
    function clampPanelToViewport(p) {
      if (!isFloating || p.style.position !== 'fixed') return;
      var vw = window.innerWidth || document.documentElement.clientWidth || 0;
      var vh = window.innerHeight || document.documentElement.clientHeight || 0;
      var w = p.offsetWidth || 1;
      var h = p.offsetHeight || 1;
      var L = parsePx(p.style.left);
      var T = parsePx(p.style.top);
      if (L === null || T === null) {
        var r0 = p.getBoundingClientRect();
        L = r0.left;
        T = r0.top;
      }
      var m = VIEWPORT_EDGE_KEEP_PX;
      var minL = m - w;
      var maxL = vw - m;
      if (maxL < minL) {
        L = (vw - w) / 2;
      } else {
        L = Math.max(minL, Math.min(maxL, L));
      }
      var minT = m - h;
      var maxT = vh - m;
      if (maxT < minT) {
        T = (vh - h) / 2;
      } else {
        T = Math.max(minT, Math.min(maxT, T));
      }
      p.style.left = L + 'px';
      p.style.top = T + 'px';
    }
    var toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'odx-debug-panel__toggle';
    toggle.setAttribute('aria-label', 'Toggle floating draggable panel mode');
    toggle.setAttribute(
      'title',
      'Float / dock: when floating, drag the panel to move; drag the right and bottom edges to resize.'
    );
    toggle.textContent = '\u3030';
    toggle.addEventListener('click', function () {
      if (isFloating) {
        removeResizeHandles(panel);
        if (placeholder && placeholder.parentNode) {
          placeholder.parentNode.replaceChild(panel, placeholder);
          placeholder = null;
        }
        panel.style.position = '';
        panel.style.left = '';
        panel.style.top = '';
        panel.style.width = '';
        panel.style.height = '';
        panel.classList.remove('odx-debug-panel--floating');
        panel.classList.remove('odx-debug-panel--dragging');
        toggle.textContent = '\u3030';
        isFloating = false;
      } else {
        var parent = panel.parentNode;
        var rect = panel.getBoundingClientRect();
        var scrollX = window.scrollX || window.pageXOffset;
        var scrollY = window.scrollY || window.pageYOffset;
        placeholder = document.createElement('span');
        placeholder.setAttribute('data-odx-panel-placeholder', '');
        placeholder.style.display = 'none';
        parent.replaceChild(placeholder, panel);
        document.body.appendChild(panel);
        panel.style.position = 'fixed';
        panel.style.left = rect.left + scrollX + 'px';
        panel.style.top = rect.top + scrollY + 'px';
        panel.style.width = (panel.offsetWidth || Math.max(rect.width, 200)) + 'px';
        panel.style.height = (panel.offsetHeight || 200) + 'px';
        panel.style.minHeight = '60px';
        panel.classList.add('odx-debug-panel--floating');
        toggle.textContent = '\u26F6';
        isFloating = true;
        addResizeHandles(panel);
        clampPanelToViewport(panel);
      }
    });
    function addResizeHandles(p) {
      if (resizeHandles.r) return;
      var r = document.createElement('div');
      r.className = 'odx-debug-panel__resize odx-debug-panel__resize--r';
      r.setAttribute('aria-hidden', 'true');
      var b = document.createElement('div');
      b.className = 'odx-debug-panel__resize odx-debug-panel__resize--b';
      b.setAttribute('aria-hidden', 'true');
      var rb = document.createElement('div');
      rb.className = 'odx-debug-panel__resize odx-debug-panel__resize--rb';
      rb.setAttribute('aria-hidden', 'true');
      p.appendChild(r);
      p.appendChild(b);
      p.appendChild(rb);
      resizeHandles.r = r;
      resizeHandles.b = b;
      resizeHandles.rb = rb;
      function startResize(ev, dir) {
        if (ev.pointerType === 'mouse' && ev.button !== 0) return;
        ev.preventDefault();
        ev.stopPropagation();
        var capEl = ev.currentTarget;
        var pid = ev.pointerId;
        var startX = ev.clientX,
          startY = ev.clientY;
        var startW = p.offsetWidth,
          startH = p.offsetHeight;
        p.classList.add('odx-debug-panel--resizing');
        addTouchScrollBlock();
        function move(e) {
          if (e.pointerId !== pid) return;
          e.preventDefault();
          if (dir === 'r' || dir === 'rb') {
            var dw = e.clientX - startX;
            p.style.width = Math.max(120, startW + dw) + 'px';
          }
          if (dir === 'b' || dir === 'rb') {
            var dh = e.clientY - startY;
            p.style.height = Math.max(60, startH + dh) + 'px';
          }
          clampPanelToViewport(p);
        }
        function up(e) {
          if (e.pointerId !== pid) return;
          try {
            if (capEl.releasePointerCapture) capEl.releasePointerCapture(pid);
          } catch (err) {}
          document.removeEventListener('pointermove', move, docOpts);
          document.removeEventListener('pointerup', up, docOpts);
          document.removeEventListener('pointercancel', up, docOpts);
          removeTouchScrollBlock();
          p.classList.remove('odx-debug-panel--resizing');
        }
        var docOpts = { capture: true };
        document.addEventListener('pointermove', move, { capture: true, passive: false });
        document.addEventListener('pointerup', up, docOpts);
        document.addEventListener('pointercancel', up, docOpts);
        try {
          if (capEl.setPointerCapture) capEl.setPointerCapture(pid);
        } catch (e2) {}
      }
      r.addEventListener('pointerdown', function (ev) {
        startResize(ev, 'r');
      });
      b.addEventListener('pointerdown', function (ev) {
        startResize(ev, 'b');
      });
      rb.addEventListener('pointerdown', function (ev) {
        startResize(ev, 'rb');
      });
    }
    function removeResizeHandles(p) {
      [resizeHandles.r, resizeHandles.b, resizeHandles.rb].forEach(function (h) {
        if (h && h.parentNode) h.parentNode.removeChild(h);
      });
      resizeHandles.r = resizeHandles.b = resizeHandles.rb = null;
    }
    if (panelBody && panelBody.parentNode === panel) {
      panel.insertBefore(toggle, panelBody);
    } else {
      panel.insertBefore(toggle, panel.firstChild);
    }
    if (typeof window.odxDebugPanelPosition === 'string' && window.odxDebugPanelPosition === 'floating') {
      toggle.click();
    }
    panel.addEventListener('pointerdown', function (ev) {
      if (!isFloating) return;
      if (ev.pointerType === 'mouse' && ev.button !== 0) return;
      if (ev.target === toggle || toggle.contains(ev.target)) return;
      if (ev.target.closest && ev.target.closest('.odx-debug-panel__resize')) return;
      var pid = ev.pointerId;
      panel.classList.add('odx-debug-panel--dragging');
      addTouchScrollBlock();
      var rect = panel.getBoundingClientRect();
      dragOffset.x = ev.clientX - rect.left;
      dragOffset.y = ev.clientY - rect.top;
      function move(e) {
        if (e.pointerId !== pid) return;
        e.preventDefault();
        panel.style.left = e.clientX - dragOffset.x + 'px';
        panel.style.top = e.clientY - dragOffset.y + 'px';
        clampPanelToViewport(panel);
      }
      function up(e) {
        if (e.pointerId !== pid) return;
        try {
          if (panel.releasePointerCapture) panel.releasePointerCapture(pid);
        } catch (err) {}
        document.removeEventListener('pointermove', move, docOpts);
        document.removeEventListener('pointerup', up, docOpts);
        document.removeEventListener('pointercancel', up, docOpts);
        removeTouchScrollBlock();
        panel.classList.remove('odx-debug-panel--dragging');
      }
      var docOpts = { capture: true };
      document.addEventListener('pointermove', move, { capture: true, passive: false });
      document.addEventListener('pointerup', up, docOpts);
      document.addEventListener('pointercancel', up, docOpts);
      try {
        if (panel.setPointerCapture) panel.setPointerCapture(pid);
      } catch (e2) {}
    });
  }
  function initAll() {
    document.querySelectorAll('.odx-debug-panel').forEach(initPanel);
  }
  /** Late-injected panels (e.g. loopback-loaded init after `odx-debug-panel-added`) or slow mobile cache. */
  window.__odxInitDebugPanelRoots = initAll;
  function scheduleLateInits() {
    [0, 120, 400, 1200, 2800].forEach(function (ms) {
      setTimeout(initAll, ms);
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
      scheduleLateInits();
    });
  } else {
    initAll();
    scheduleLateInits();
  }
  document.addEventListener('odx-debug-panel-added', function (ev) {
    if (ev.target && ev.target.classList && ev.target.classList.contains('odx-debug-panel')) initPanel(ev.target);
  });
})();
